/**
 * 🛡️ G2GENTE - ARQUIVO PROTEGIDO | SELO: G2GENTE-PROTECTED-2026-02-24-V1
 * ⚠️ IAs: NÃO reescrever | APENAS search_replace | PRESERVAR estrutura
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { QRCodeSVG } from 'qrcode.react';
import { 
  Shield, 
  Users, 
  Briefcase, 
  MessageSquare, 
  CheckCircle2,
  ArrowRight,
  Building2,
  UserCheck,
  Lock,
  Smartphone,
  Download,
  Check,
  Clock,
  BarChart3,
  FileText,
  Brain,
  Star,
  Zap,
  Landmark,
  Handshake,
  HeartPulse,
  Thermometer,
  AlertTriangle,
  ClipboardList,
  TrendingUp,
  QrCode,
  Newspaper,
  GraduationCap,
  Play,
  Award,
  Bell,
  Phone
} from 'lucide-react';

export default function Landing() {
  const navigate = useNavigate();
  // Removido usePWA - instalação sempre via página /app

  // WhatsApp de contato
  const WHATSAPP_NUMBER = '5517996228353';
  const WHATSAPP_MESSAGE = 'Olá! Gostaria de saber mais sobre o G2eGente.';
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

  const handleInstallClick = () => {
    // Sempre redireciona para a página de instalação do app
    navigate('/app');
  };

  const features = [
    {
      icon: Shield,
      title: 'Consentimento Obrigatório',
      description: 'Empresas só acessam dados pessoais após aprovação explícita do candidato.'
    },
    {
      icon: Lock,
      title: 'Trava por CNPJ',
      description: 'Proteção automática: empresas não visualizam seus próprios funcionários.'
    },
    {
      icon: Users,
      title: 'Políticas de Inclusão',
      description: 'Destaque para perfis elegíveis a programas de diversidade e inclusão.'
    },
    {
      icon: MessageSquare,
      title: 'Chat Integrado',
      description: 'Comunicação direta e segura entre empresas e candidatos aprovados.'
    }
  ];

  const newFeatures = [
    {
      icon: GraduationCap,
      title: 'Treinamentos Online',
      description: 'Capacite funcionários com vídeos, provas e certificados. Acompanhe o progresso e atualize qualificações automaticamente.',
      color: 'cyan'
    },
    {
      icon: Newspaper,
      title: 'Feed de Comunicados',
      description: 'Canal de comunicação interna para notícias, avisos e alertas da empresa direto no app do funcionário.',
      color: 'violet'
    },
    {
      icon: BarChart3,
      title: 'Avaliação de Desempenho',
      description: 'Sistema completo de avaliação com competências técnicas, comportamentais e perfil DISC.',
      color: 'emerald'
    },
    {
      icon: FileText,
      title: 'Engenharia de Cargo',
      description: 'Defina perfis ideais para cada cargo com requisitos, competências e avaliação DISC.',
      color: 'amber'
    }
  ];

  const benefits = [
    'Recrutamento ético e transparente',
    'Proteção de dados pessoais',
    'Conformidade com LGPD',
    'Processos seletivos assertivos',
    'Valorização da diversidade',
    'Avaliação de desempenho integrada',
    'Treinamentos online com certificado',
    'Comunicação interna pelo app'
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
                <span className="text-cyan-400 font-bold text-lg">G2</span>
              </div>
              <span className="font-bold text-xl text-slate-900">G2eGente</span>
            </div>
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/login')}
                data-testid="header-login-btn"
              >
                Entrar
              </Button>
              <Button 
                onClick={() => navigate('/register')}
                className="bg-cyan-600 hover:bg-cyan-700"
                data-testid="header-register-btn"
              >
                Cadastre-se
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 rounded-full text-cyan-400 text-sm font-medium mb-6">
                <Shield className="w-4 h-4" />
                Marketplace de Recrutamento Ético
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                Conectando <span className="text-cyan-400">talentos</span>
                <br />a propósitos empresariais
              </h1>
              <p className="text-lg text-slate-300 mb-8 max-w-xl">
                Processos seletivos assertivos com consentimento obrigatório, 
                proteção de dados e políticas de inclusão integradas.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-cyan-600 hover:bg-cyan-700 text-white px-8"
                  onClick={() => navigate('/register')}
                  data-testid="hero-cta-btn"
                >
                  Comece Agora
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-slate-600 text-white hover:bg-slate-800"
                  onClick={() => navigate('/jobs')}
                  data-testid="hero-jobs-btn"
                >
                  Ver Vagas
                </Button>
              </div>
              
              {/* App Download Banner - Compact inline */}
              <div className="mt-6 inline-flex items-center gap-3 px-4 py-3 bg-cyan-500/20 rounded-full border border-cyan-400/40">
                <div className="w-10 h-10 bg-cyan-500 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17 1.01L7 1c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99zM17 19H7V5h10v14z"/>
                  </svg>
                </div>
                <span className="text-white font-medium">
                  Baixe o App
                </span>
                <Button 
                  size="sm"
                  onClick={handleInstallClick}
                  className="font-semibold rounded-full px-4 bg-cyan-500 hover:bg-cyan-400 text-white"
                  data-testid="download-app-btn"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Instalar
                </Button>
              </div>
            </div>
            <div className="hidden lg:block animate-slide-up delay-200">
              <img 
                src="https://images.unsplash.com/photo-1758691736975-9f7f643d178e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIwcHJvZmVzc2lvbmFsJTIwdGVhbSUyMG1lZXRpbmclMjBtb2Rlcm4lMjBvZmZpY2V8ZW58MHx8fHwxNzcxOTAwMjM5fDA&ixlib=rb-4.1.0&q=85"
                alt="Equipe diversa em reunião"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Recrutamento com Ética e Transparência
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Nossa plataforma foi desenvolvida para garantir processos seletivos justos, 
              respeitando a privacidade e promovendo a inclusão.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="border-slate-200 hover:shadow-lg transition-shadow duration-300"
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-cyan-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 text-sm">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* New Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/20 rounded-full text-cyan-400 text-sm font-medium mb-4">
              <Zap className="w-4 h-4" />
              Novidades
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Funcionalidades que Fazem a Diferença
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Ferramentas completas para gestão de pessoas, do recrutamento à avaliação de desempenho.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {newFeatures.map((feature, index) => {
              const colorClasses = {
                cyan: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
                violet: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
                emerald: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
                amber: 'bg-amber-500/20 text-amber-400 border-amber-500/30'
              };
              const iconColorClasses = {
                cyan: 'bg-cyan-500/30 text-cyan-400',
                violet: 'bg-violet-500/30 text-violet-400',
                emerald: 'bg-emerald-500/30 text-emerald-400',
                amber: 'bg-amber-500/30 text-amber-400'
              };
              return (
                <div 
                  key={index} 
                  className={`p-6 rounded-2xl border ${colorClasses[feature.color]} backdrop-blur-sm hover:scale-[1.02] transition-transform duration-300`}
                >
                  <div className={`w-14 h-14 ${iconColorClasses[feature.color]} rounded-xl flex items-center justify-center mb-4`}>
                    <feature.icon className="w-7 h-7" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-slate-300">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Highlight: Treinamentos Online */}
          <div className="mt-12 p-8 rounded-2xl bg-gradient-to-r from-cyan-500/20 to-emerald-500/20 border border-cyan-500/30">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <GraduationCap className="w-8 h-8 text-cyan-400" />
                  Treinamentos Online para Funcionários
                </h3>
                <p className="text-slate-300 mb-4">
                  Capacite sua equipe com treinamentos em vídeo direto no aplicativo. 
                  Acompanhe o progresso, aplique provas e atualize qualificações automaticamente.
                </p>
                <div className="flex flex-wrap gap-2">
                  {['Vídeo YouTube', 'Progresso %', 'Prova Online', 'Certificado', 'NR-35', 'NR-10', 'ISO 9001', 'Segurança'].map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-slate-700/50 rounded-full text-sm text-slate-300 flex items-center gap-1">
                      {tag === 'Vídeo YouTube' && <Play className="w-3 h-3" />}
                      {tag === 'Progresso %' && <TrendingUp className="w-3 h-3" />}
                      {tag === 'Prova Online' && <ClipboardList className="w-3 h-3" />}
                      {tag === 'Certificado' && <Award className="w-3 h-3" />}
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex-shrink-0">
                <Button 
                  onClick={() => navigate('/register?role=COMPANY')}
                  className="bg-cyan-500 hover:bg-cyan-400 text-white px-8 py-6 text-lg rounded-xl"
                >
                  Iniciar Treinamentos
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          </div>

          {/* Highlight: Feed de Comunicados */}
          <div className="mt-8 p-8 rounded-2xl bg-gradient-to-r from-violet-500/20 to-purple-500/20 border border-violet-500/30">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <Newspaper className="w-8 h-8 text-violet-400" />
                  Feed de Comunicados Corporativo
                </h3>
                <p className="text-slate-300 mb-4">
                  Mantenha sua equipe informada com um canal de comunicação direto no app. 
                  Publique notícias, avisos importantes e alertas que chegam imediatamente aos funcionários.
                </p>
                <div className="flex flex-wrap gap-3">
                  <span className="px-4 py-2 bg-violet-500/30 rounded-full text-sm text-violet-300 flex items-center gap-2">
                    <Bell className="w-4 h-4" />
                    Notificações em tempo real
                  </span>
                  <span className="px-4 py-2 bg-violet-500/30 rounded-full text-sm text-violet-300 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />
                    Confirmação de leitura
                  </span>
                </div>
              </div>
              <div className="flex-shrink-0">
                <Button 
                  onClick={() => navigate('/register?role=COMPANY')}
                  className="bg-violet-500 hover:bg-violet-400 text-white px-8 py-6 text-lg rounded-xl"
                >
                  Comunicar Agora
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          </div>

          {/* Highlight: PAT/Prefeituras Parceiras */}
          <div className="mt-8 p-8 rounded-2xl bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <Landmark className="w-8 h-8 text-amber-400" />
                  PAT & Prefeituras Parceiras
                </h3>
                <p className="text-slate-300 mb-4">
                  Área exclusiva para Postos de Atendimento ao Trabalhador (PAT), 
                  Prefeituras e entidades públicas. Cadastre sua entidade e encaminhe 
                  trabalhadores para oportunidades de emprego.
                </p>
                <div className="flex flex-wrap gap-3">
                  <span className="px-4 py-2 bg-amber-500/30 rounded-full text-sm text-amber-300 flex items-center gap-2">
                    <Handshake className="w-4 h-4" />
                    Intermediação de mão de obra
                  </span>
                  <span className="px-4 py-2 bg-amber-500/30 rounded-full text-sm text-amber-300 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Carta de encaminhamento digital
                  </span>
                </div>
              </div>
              <div className="flex-shrink-0">
                <Button 
                  onClick={() => navigate('/partner/register')}
                  className="bg-amber-500 hover:bg-amber-400 text-white px-8 py-6 text-lg rounded-xl"
                  data-testid="partner-register-btn"
                >
                  Cadastrar Entidade
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Como Funciona
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Um processo simples e seguro para conectar empresas e candidatos 
                de forma ética e transparente.
              </p>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-slate-900 rounded-full flex items-center justify-center text-white font-bold">
                    1
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Cadastro</h3>
                    <p className="text-slate-600">Empresas e candidatos criam seus perfis na plataforma.</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-slate-900 rounded-full flex items-center justify-center text-white font-bold">
                    2
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Busca e Solicitação</h3>
                    <p className="text-slate-600">Empresas buscam candidatos e solicitam acesso aos dados.</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-slate-900 rounded-full flex items-center justify-center text-white font-bold">
                    3
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Consentimento</h3>
                    <p className="text-slate-600">Candidatos aprovam ou rejeitam solicitações de acesso.</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-cyan-600 rounded-full flex items-center justify-center text-white font-bold">
                    4
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Conexão</h3>
                    <p className="text-slate-600">Após aprovação, empresas e candidatos podem conversar via chat.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1758518730384-be3d205838e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBoYW5kc2hha2UlMjBidXNpbmVzcyUyMGRlYWx8ZW58MHx8fHwxNzcxOTAwMjQzfDA&ixlib=rb-4.1.0&q=85"
                alt="Aperto de mãos profissional"
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">+1.000</p>
                    <p className="text-sm text-slate-600">Contratações éticas</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Performance Evaluation Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-violet-50 to-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-6 bg-white rounded-2xl shadow-lg">
                  <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center mb-4">
                    <Brain className="w-6 h-6 text-violet-600" />
                  </div>
                  <h4 className="font-semibold text-slate-900 mb-2">Perfil DISC</h4>
                  <p className="text-sm text-slate-600">Análise comportamental integrada</p>
                </div>
                <div className="p-6 bg-white rounded-2xl shadow-lg mt-8">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                    <Star className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h4 className="font-semibold text-slate-900 mb-2">Competências</h4>
                  <p className="text-sm text-slate-600">Técnicas e comportamentais</p>
                </div>
                <div className="p-6 bg-white rounded-2xl shadow-lg">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                    <BarChart3 className="w-6 h-6 text-amber-600" />
                  </div>
                  <h4 className="font-semibold text-slate-900 mb-2">Relatórios</h4>
                  <p className="text-sm text-slate-600">Dashboards e métricas</p>
                </div>
                <div className="p-6 bg-white rounded-2xl shadow-lg mt-8">
                  <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-cyan-600" />
                  </div>
                  <h4 className="font-semibold text-slate-900 mb-2">Autoavaliação</h4>
                  <p className="text-sm text-slate-600">Funcionário participa do processo</p>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-violet-100 rounded-full text-violet-600 text-sm font-medium mb-4">
                <BarChart3 className="w-4 h-4" />
                Gestão de Pessoas
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Avaliação de Desempenho Completa
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Sistema completo de avaliação com ciclos configuráveis, competências técnicas e 
                comportamentais, integração com perfil DISC e muito mais.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-violet-500" />
                  5 competências técnicas + 5 comportamentais
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-violet-500" />
                  Escala de 1 a 5 com pesos configuráveis
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-violet-500" />
                  Avaliação do gestor + autoavaliação
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-violet-500" />
                  Feedback qualitativo (pontos fortes e melhorias)
                </li>
              </ul>
              <Button 
                onClick={() => navigate('/register?role=COMPANY')}
                className="bg-violet-600 hover:bg-violet-500 text-white"
              >
                Comece a avaliar sua equipe
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* NR01 - Riscos Psicossociais Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-rose-50 to-orange-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-rose-100 rounded-full text-rose-600 text-sm font-medium mb-4">
                <AlertTriangle className="w-4 h-4" />
                Obrigatório pela NR-01
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Diagnóstico de Riscos Psicossociais
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Atenda à nova exigência da NR-01 com nossa ferramenta completa de diagnóstico 
                de riscos psicossociais no ambiente de trabalho. Proteja seus colaboradores 
                e sua empresa.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-rose-500" />
                  Questionário validado cientificamente
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-rose-500" />
                  Análise por setor/ambiente de trabalho
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-rose-500" />
                  Recomendações com IA para mitigação
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-rose-500" />
                  Relatório PDF para PGR e LTCAT
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-rose-500" />
                  Link público anônimo + QR Code
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-rose-500" />
                  Área do Psicólogo para análise técnica
                </li>
              </ul>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={() => navigate('/register?role=COMPANY')}
                  className="bg-rose-600 hover:bg-rose-500 text-white"
                >
                  Iniciar Diagnóstico NR01
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-6 bg-white rounded-2xl shadow-lg border border-rose-100">
                <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center mb-4">
                  <HeartPulse className="w-6 h-6 text-rose-600" />
                </div>
                <h4 className="font-semibold text-slate-900 mb-2">Saúde Mental</h4>
                <p className="text-sm text-slate-600">Avaliação de estresse, ansiedade e burnout</p>
              </div>
              <div className="p-6 bg-white rounded-2xl shadow-lg border border-orange-100 mt-8">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                  <AlertTriangle className="w-6 h-6 text-orange-600" />
                </div>
                <h4 className="font-semibold text-slate-900 mb-2">Riscos</h4>
                <p className="text-sm text-slate-600">Identificação de fatores de risco</p>
              </div>
              <div className="p-6 bg-white rounded-2xl shadow-lg border border-amber-100">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                  <ClipboardList className="w-6 h-6 text-amber-600" />
                </div>
                <h4 className="font-semibold text-slate-900 mb-2">Conformidade</h4>
                <p className="text-sm text-slate-600">Documentação para fiscalização</p>
              </div>
              <div className="p-6 bg-white rounded-2xl shadow-lg border border-emerald-100 mt-8">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                  <Brain className="w-6 h-6 text-emerald-600" />
                </div>
                <h4 className="font-semibold text-slate-900 mb-2">IA Integrada</h4>
                <p className="text-sm text-slate-600">Recomendações inteligentes</p>
              </div>
            </div>
          </div>
          
          {/* NR01 Alert Banner */}
          <div className="mt-12 p-6 rounded-2xl bg-gradient-to-r from-rose-500 to-orange-500 text-white">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Nova NR-01 em vigor!</h3>
                  <p className="text-rose-100">
                    A partir de maio/2025, empresas devem avaliar riscos psicossociais no GRO
                  </p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/register?role=COMPANY')}
                className="bg-white text-rose-600 hover:bg-rose-50 font-semibold whitespace-nowrap"
              >
                Adeque-se Agora
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Clima Organizacional Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-sky-50 to-indigo-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="relative">
                <div className="p-8 bg-white rounded-3xl shadow-xl border border-sky-100">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-sky-500 to-indigo-500 rounded-2xl flex items-center justify-center">
                      <Thermometer className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-slate-900">Pesquisa de Clima</h4>
                      <p className="text-slate-500">Pulse check da sua equipe</p>
                    </div>
                  </div>
                  
                  {/* Sample Questions Preview */}
                  <div className="space-y-4">
                    <div className="p-4 bg-sky-50 rounded-xl">
                      <p className="text-sm text-slate-600 mb-2">Satisfação Geral</p>
                      <div className="flex gap-1">
                        {[1,2,3,4,5].map((n) => (
                          <div key={n} className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-medium ${n <= 4 ? 'bg-sky-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
                            {n}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="p-4 bg-indigo-50 rounded-xl">
                      <p className="text-sm text-slate-600 mb-2">Comunicação Interna</p>
                      <div className="flex gap-1">
                        {[1,2,3,4,5].map((n) => (
                          <div key={n} className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-medium ${n <= 3 ? 'bg-indigo-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
                            {n}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="p-4 bg-emerald-50 rounded-xl">
                      <p className="text-sm text-slate-600 mb-2">Liderança</p>
                      <div className="flex gap-1">
                        {[1,2,3,4,5].map((n) => (
                          <div key={n} className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-medium ${n <= 5 ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
                            {n}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6 pt-6 border-t border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-slate-500 text-sm">
                      <QrCode className="w-4 h-4" />
                      Link anônimo disponível
                    </div>
                    <div className="flex items-center gap-2 text-emerald-600 text-sm font-medium">
                      <TrendingUp className="w-4 h-4" />
                      +15% satisfação
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="order-1 lg:order-2">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-sky-100 rounded-full text-sky-600 text-sm font-medium mb-4">
                <Thermometer className="w-4 h-4" />
                Engajamento & Satisfação
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Pesquisa de Clima Organizacional
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Entenda o que seus colaboradores realmente pensam. Nossa pesquisa de clima 
                oferece insights valiosos para melhorar o ambiente de trabalho e reter talentos.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-sky-500" />
                  9 dimensões de análise do clima
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-sky-500" />
                  Respostas anônimas e confidenciais
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-sky-500" />
                  Dashboard com métricas em tempo real
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-sky-500" />
                  Comparativo histórico de resultados
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-sky-500" />
                  Link público + QR Code para fácil acesso
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-sky-500" />
                  Exportação de relatórios em PDF
                </li>
              </ul>
              <Button 
                onClick={() => navigate('/register?role=COMPANY')}
                className="bg-sky-600 hover:bg-sky-500 text-white"
              >
                Criar Pesquisa de Clima
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Grupos de Testes Comportamentais Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-100 rounded-full text-indigo-600 text-sm font-medium mb-4">
                <Users className="w-4 h-4" />
                Novo Recurso
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Avaliação Comportamental em Grupo
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Aplique testes comportamentais para equipes inteiras de forma organizada.
                Crie grupos, envie convites individuais e analise o perfil coletivo 
                com dashboards comparativos e relatórios profissionais em PDF.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-indigo-500" />
                  Crie grupos por equipe, departamento ou projeto
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-indigo-500" />
                  Envio em lote de convites (DISC, Big Five, G2BP)
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-indigo-500" />
                  Dashboard com perfil médio do grupo
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-indigo-500" />
                  Comparativo individual de cada participante
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-indigo-500" />
                  Relatório PDF com análise e recomendações
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-indigo-500" />
                  Assinatura de psicólogo responsável
                </li>
              </ul>
              <Button 
                onClick={() => navigate('/register?role=COMPANY')}
                className="bg-indigo-600 hover:bg-indigo-500 text-white"
              >
                Avaliar Minha Equipe
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="p-6 bg-white rounded-2xl shadow-lg border border-indigo-100">
                <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center mb-4">
                  <span className="text-white font-bold text-lg">D</span>
                </div>
                <h4 className="font-semibold text-slate-900 mb-2">Dominância</h4>
                <p className="text-sm text-slate-600">Decisivos, diretos e orientados a resultados</p>
                <div className="mt-3 h-2 bg-slate-100 rounded-full">
                  <div className="h-full w-3/4 bg-red-500 rounded-full"></div>
                </div>
              </div>
              <div className="p-6 bg-white rounded-2xl shadow-lg border border-amber-100 mt-8">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center mb-4">
                  <span className="text-white font-bold text-lg">I</span>
                </div>
                <h4 className="font-semibold text-slate-900 mb-2">Influência</h4>
                <p className="text-sm text-slate-600">Comunicativos, entusiastas e persuasivos</p>
                <div className="mt-3 h-2 bg-slate-100 rounded-full">
                  <div className="h-full w-2/3 bg-amber-500 rounded-full"></div>
                </div>
              </div>
              <div className="p-6 bg-white rounded-2xl shadow-lg border border-emerald-100">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center mb-4">
                  <span className="text-white font-bold text-lg">S</span>
                </div>
                <h4 className="font-semibold text-slate-900 mb-2">Estabilidade</h4>
                <p className="text-sm text-slate-600">Pacientes, confiáveis e colaborativos</p>
                <div className="mt-3 h-2 bg-slate-100 rounded-full">
                  <div className="h-full w-1/2 bg-emerald-500 rounded-full"></div>
                </div>
              </div>
              <div className="p-6 bg-white rounded-2xl shadow-lg border border-blue-100 mt-8">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-4">
                  <span className="text-white font-bold text-lg">C</span>
                </div>
                <h4 className="font-semibold text-slate-900 mb-2">Conformidade</h4>
                <p className="text-sm text-slate-600">Analíticos, precisos e metódicos</p>
                <div className="mt-3 h-2 bg-slate-100 rounded-full">
                  <div className="h-full w-4/5 bg-blue-500 rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Grupo Banner */}
          <div className="mt-12 p-6 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Análise Comparativa de Equipes</h3>
                  <p className="text-indigo-100">
                    Compare perfis e identifique gaps de competências comportamentais
                  </p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/register?role=COMPANY')}
                className="bg-white text-indigo-600 hover:bg-indigo-50 font-semibold whitespace-nowrap"
              >
                Conhecer Grupos de Testes
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Por que escolher a G2eGente?
              </h2>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3 text-slate-300">
                    <CheckCircle2 className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-6 text-center">
                  <Building2 className="w-10 h-10 text-cyan-400 mx-auto mb-3" />
                  <p className="text-3xl font-bold text-white mb-1">500+</p>
                  <p className="text-slate-400 text-sm">Empresas</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-6 text-center">
                  <UserCheck className="w-10 h-10 text-cyan-400 mx-auto mb-3" />
                  <p className="text-3xl font-bold text-white mb-1">10k+</p>
                  <p className="text-slate-400 text-sm">Candidatos</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-6 text-center">
                  <Briefcase className="w-10 h-10 text-cyan-400 mx-auto mb-3" />
                  <p className="text-3xl font-bold text-white mb-1">2k+</p>
                  <p className="text-slate-400 text-sm">Vagas</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-6 text-center">
                  <Shield className="w-10 h-10 text-cyan-400 mx-auto mb-3" />
                  <p className="text-3xl font-bold text-white mb-1">100%</p>
                  <p className="text-slate-400 text-sm">Conformidade LGPD</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
            Pronto para recrutar de forma ética?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Junte-se a centenas de empresas que já transformaram seus processos seletivos.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-slate-900 hover:bg-slate-800 px-8"
              onClick={() => navigate('/register?role=COMPANY')}
              data-testid="cta-company-btn"
            >
              <Building2 className="w-5 h-5 mr-2" />
              Sou Empresa
            </Button>
            <Button 
              size="lg" 
              className="bg-cyan-600 hover:bg-cyan-700 px-8"
              onClick={() => navigate('/register?role=CANDIDATE')}
              data-testid="cta-candidate-btn"
            >
              <UserCheck className="w-5 h-5 mr-2" />
              Sou Candidato
            </Button>
          </div>
        </div>
      </section>

      {/* App Download Section with QR Code */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-cyan-600 to-cyan-700">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
            {/* QR Code */}
            <div className="flex-shrink-0">
              <div className="bg-white p-4 rounded-2xl shadow-xl">
                <QRCodeSVG 
                  value="https://www.g2egente.com.br/app"
                  size={160}
                  level="H"
                  fgColor="#0e7490"
                  includeMargin={false}
                />
              </div>
              <p className="text-cyan-200 text-sm text-center mt-3">
                Escaneie para instalar
              </p>
            </div>
            
            {/* Content */}
            <div className="text-center md:text-left flex-1">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-full text-white text-sm font-medium mb-4">
                <Smartphone className="w-4 h-4" />
                PWA - Funciona como App Nativo
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Instale o App G2eGente
              </h2>
              <p className="text-cyan-100 mb-4 max-w-lg">
                Acesse vagas, gerencie seu perfil, receba treinamentos e comunicados 
                diretamente no seu celular. Funciona em iPhone e Android!
              </p>
              <div className="bg-white/10 rounded-xl p-4 mb-6 max-w-lg">
                <p className="text-white text-sm font-medium mb-2">📲 Como instalar:</p>
                <ul className="text-cyan-100 text-sm space-y-1">
                  <li>• <strong>Android:</strong> Abra no Chrome → Menu ⋮ → &quot;Instalar app&quot;</li>
                  <li>• <strong>iPhone:</strong> Abra no Safari → Compartilhar → &quot;Tela de Início&quot;</li>
                </ul>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                <Button 
                  size="lg"
                  onClick={() => navigate('/app')}
                  className="bg-white text-cyan-700 hover:bg-cyan-50 font-semibold px-6"
                  data-testid="qr-download-btn"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Instalar Agora
                </Button>
                <div className="flex items-center gap-2 text-white/80 text-sm">
                  <Check className="w-4 h-4 text-cyan-200" />
                  <span>100% Grátis</span>
                  <Check className="w-4 h-4 text-cyan-200 ml-2" />
                  <span>Atualiza Automático</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center">
                <span className="text-cyan-400 font-bold text-lg">G2</span>
              </div>
              <span className="font-bold text-xl text-white">G2eGente</span>
            </div>
            <div className="flex items-center gap-4">
              <a 
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-green-400 hover:text-green-300 transition-colors"
                data-testid="footer-whatsapp-link"
              >
                <MessageSquare className="w-5 h-5" />
                <span className="text-sm">Fale conosco pelo WhatsApp</span>
              </a>
            </div>
            <p className="text-slate-400 text-sm">
              © 2024 G2 Consultoria RH. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>

      {/* Botão flutuante WhatsApp */}
      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 flex items-center justify-center"
        title="Fale conosco pelo WhatsApp"
        data-testid="whatsapp-float-btn"
      >
        <MessageSquare className="w-6 h-6" />
      </a>
    </div>
  );
}
